import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { Controller, type Control } from 'react-hook-form'
import type {
  AbsenceImportFormValues,
  AnalyzePayload,
  AbsenceImportTypeMapping,
} from '../types'

interface Props {
  control: Control<AbsenceImportFormValues>
  analysis: AnalyzePayload
  absenceTypes: { id: string; name: string }[]
  typeMappings: AbsenceImportTypeMapping[]
  onNext(): void
  onBack(): void
}

export function StepTypeMapping({
  control,
  analysis,
  absenceTypes,
  typeMappings,
  onNext,
  onBack,
}: Props) {
  const translations = useTranslate({
    importedType: 'settings.absence.import.imported-type',
    count: 'settings.absence.import.count',
    mapTo: 'settings.absence.import.map-to',
    selectAbsenceType: 'settings.absence.import.select-absence-type',
    next: 'settings.absence.absence-type.next',
    back: 'common.back',
  })

  return (
    <div className="grid gap-3 rounded-md border border-stone-200 p-3">
      <div className="grid items-center gap-2 text-xs font-semibold uppercase tracking-wide text-stone-500 md:grid-cols-[2fr_100px_2fr]">
        <span>{translations.importedType}</span>
        <span>{translations.count}</span>
        <span>{translations.mapTo}</span>
      </div>

      {analysis.importedAbsenceTypes.map((item, index) => (
        <div
          key={item.importedValue}
          className="grid items-center gap-2 md:grid-cols-[2fr_100px_2fr]"
        >
          <div className="rounded border border-stone-200 px-2 py-2 text-sm">
            {item.importedValue}
          </div>

          <div className="text-sm text-stone-600">{item.count}</div>

          <Controller
            control={control}
            name={`typeMappings.${index}.absenceTypeId`}
            render={({ field }) => (
              <select
                className={cn(
                  'h-10 rounded-md border px-3 text-sm',
                  field.value ? 'border-stone-300' : 'border-amber-400'
                )}
                value={field.value ?? typeMappings[index]?.absenceTypeId ?? ''}
                onChange={field.onChange}
              >
                <option value="">{translations.selectAbsenceType}</option>
                {absenceTypes.map(absenceType => (
                  <option key={absenceType.id} value={absenceType.id}>
                    {absenceType.name}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
      ))}

      <div className="mt-2 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack}>
          {translations.back}
        </Button>

        <Button size="sm" onClick={onNext}>
          {translations.next}
        </Button>
      </div>
    </div>
  )
}
